import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import OutsideClickObserver from 'components/shared/OutsideClickObserver';

// #region styled-components

const Container = styled.div`
  position: relative;
  display: inline-block;

  ${(props) => props.containerCss}
`;

const Menu = styled.div`
  position: absolute;
  top: 32px;
  z-index: 10;
  min-width: ${(props) => `${props.width}px`};
  padding: var(--cc_size_spacing_s);
  border-radius: var(--cc_size_border_radius_l);
  background: var(--cc_color_background_1);
  box-shadow: 0px 0px 7px rgba(0, 0, 0, 0.04), 0 0px 13.2px rgba(0, 0, 0, 0.04),
    0px 0px 18px rgba(0, 0, 0, 0.07), 0px 0px 34px rgba(0, 0, 0, 0.13);

  ${(props) => props.menuCss}

  hr {
    margin: var(--cc_size_spacing_xs) 0;
    height: 1px;
    border: 0;
    background: var(--cc_color_border_default);
  }
`;

const TriggerButton = styled.button`
  padding: 0;
  border: none;
  background: none;
`;

// #endregion

const messages = defineMessages({
  defaultAriaLabel: {
    id: 'Dropdown.trigger.defaultAriaLabel',
    defaultMessage: 'Open Options Menu',
    description: 'Default aria label for dropdown trigger',
  },
});

const getWidth = (value) => {
  switch (value) {
    case 'small':
      return 160;
    case 'medium':
      return 235;
    case 'wide':
      return 250;
    default:
      return 0;
  }
};

/**
 * @deprecated Use { Menu } from `@companycam/slab-web` instead
 *
 * https://slab-web.cocam.co/?path=/docs/menu--docs
 */

const Dropdown = ({
  triggerAriaLabel,
  size,
  triggerContent,
  children,
  style,
  menuCss,
  containerCss,
  customTrigger,
  enabled = true,
  stopPropagationOnTrigger = true,
  triggerTestId = 'dropdown__trigger-button',
}) => {
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const close = () => {
    setIsOpen(false);
  };
  const width = getWidth(size);

  const handleTriggerClicked = (e) => {
    if (stopPropagationOnTrigger) {
      e.stopPropagation();
    }

    if (enabled) {
      setIsOpen((prevState) => !prevState);
    }
  };

  const triggerProps = {
    tabIndex: '0',
    role: 'button',
    'aria-haspopup': 'true',
    'aria-expanded': isOpen,
    type: 'button',
    onClick: handleTriggerClicked,
    width,
    'aria-label': triggerAriaLabel || formatMessage(messages.defaultAriaLabel),
  };

  const renderTrigger = () => {
    if (customTrigger) {
      return customTrigger({ closeMenu: close, isOpen, triggerProps });
    }

    return (
      <TriggerButton
        tabIndex={triggerProps.tabIndex}
        role={triggerProps.role}
        aria-haspopup={triggerProps['aria-haspopup']}
        aria-expanded={triggerProps['aria-expanded']}
        type={triggerProps.type}
        onClick={triggerProps.onClick}
        width={triggerProps.width}
        aria-label={triggerProps['aria-label']}
        data-testid={triggerTestId}
      >
        {typeof triggerContent === 'function'
          ? triggerContent({ closeMenu: close, isOpen })
          : triggerContent}
      </TriggerButton>
    );
  };

  return (
    <OutsideClickObserver active={isOpen} onClickOutside={close}>
      <Container containerCss={containerCss}>
        {renderTrigger()}
        {isOpen ? (
          <Menu role="menu" width={width} style={style} menuCss={menuCss}>
            {typeof children === 'function'
              ? children({ closeMenu: close })
              : children}
          </Menu>
        ) : null}
      </Container>
    </OutsideClickObserver>
  );
};

Dropdown.propTypes = {
  size: PropTypes.string,
  triggerContent: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.func,
  ]),
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  style: PropTypes.object,
  enabled: PropTypes.bool,
  menuCss: PropTypes.array,
  customTrigger: PropTypes.func,
  containerCss: PropTypes.array,
  triggerAriaLabel: PropTypes.string,
  stopPropagationOnTrigger: PropTypes.bool,
  triggerTestId: PropTypes.string,
};

export default Dropdown;
